/**
 * ArchiveRoleModal.jsx — Employer v1.1 Phase 0.6 Slice D.
 *
 * Confirmation modal wrapping POST /api/employer/roles/{id}/archive.
 * Two distinct surfaces driven by the role's current `status`:
 *
 *   - status='draft' → DISCARD draft
 *     Permanently removes the draft. There is no un-archive.
 *
 *   - status='closed' → TERMINAL archive
 *     Permanently archives a closed role. There is no un-archive.
 *
 * Buttons are `disabled` while the request is in flight (W-3 guard).
 * Backend error `detail` is surfaced verbatim.
 */
import { Archive, Loader2, ShieldAlert, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Operator-locked copy. Tests pin the substrings.
export const DISCARD_DRAFT_COPY =
  'Archiving a draft permanently discards it. There is no un-archive.';
export const TERMINAL_ARCHIVE_COPY =
  'Archiving a closed role is permanent and removes it from the active list. There is no un-archive.';

export default function ArchiveRoleModal({
  open,
  status,           // 'draft' | 'closed' (other statuses cannot reach this modal)
  onCancel,
  onConfirm,
  pending,
  error,
}) {
  const isDraft = status === 'draft';
  const title   = isDraft ? 'Discard this draft?' : 'Archive this closed role?';
  const copy    = isDraft ? DISCARD_DRAFT_COPY : TERMINAL_ARCHIVE_COPY;
  const cta     = isDraft ? 'Discard draft' : 'Archive role';
  const Icon    = isDraft ? Trash2 : Archive;

  return (
    <Dialog
      open={Boolean(open)}
      onOpenChange={(o) => { if (!o && !pending) onCancel(); }}
    >
      <DialogContent
        data-testid="archive-confirm-modal"
        aria-busy={pending ? 'true' : 'false'}
      >
        <DialogHeader>
          <DialogTitle data-testid="archive-confirm-title">{title}</DialogTitle>
          <DialogDescription data-testid="archive-confirm-copy">
            {copy}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            className="flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200"
            data-testid="archive-confirm-error"
          >
            <ShieldAlert className="mt-0.5 h-4 w-4" />
            <div>{error}</div>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={pending}
            data-testid="archive-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            variant={isDraft ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={pending}
            data-testid="archive-confirm-go"
          >
            {pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Archiving…
              </>
            ) : (
              <>
                <Icon className="mr-2 h-4 w-4" />
                {cta}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
